import type { Location } from 'react-router-dom';
import { historyIndex } from '@/shell/backNavigation';
import { previousAddressOf } from '@/shell/historyBackLabel';
import { isWorkAreaOverviewPath } from '@/features/work-areas/workAreaOverviewUrls';

/**
 * Where a full page was opened from (I-1205), as the history entry's state carries it: the types and
 * the one reader the shell needs at boot. Kept apart from `fullPage.tsx`, whose links and hooks only an
 * item's page needs: `Layout` reads `readCameFrom` on every screen, and importing it from there put the
 * whole of `fullPage.tsx` in the launch tab's entry chunk (T-0559 entry budget, gate 474).
 */
export type FullPageScopeType =
  | { readonly kind: 'all' }
  | { readonly kind: 'namespace'; readonly namespaceId: string }
  | { readonly kind: 'work-area'; readonly workAreaId: string };

export interface IFullPageOrigin {
  readonly pathname: string;
  readonly search: string;
  readonly hash: string;
  /** The history entry the panel was on, so Back can tell the page is still one step past it. */
  readonly historyIndex: number;
  readonly scope: FullPageScopeType | null;
}

export interface IFullPageState {
  readonly cameFrom: IFullPageOrigin;
}

/** The origin a page was opened from, or `null` (a deep link, a new tab: nothing to go back to). */
export function readCameFrom(state: unknown): IFullPageOrigin | null {
  const candidate = (state as Partial<IFullPageState> | null)?.cameFrom;
  return candidate && typeof candidate.pathname === 'string' ? candidate : null;
}

/** The scope the origin was in, as the project store says it now. */
export function currentScope(store: {
  allWorkAreas: boolean;
  selectedNamespaceId: string | null;
  selectedProjectId: string | null;
}): FullPageScopeType | null {
  if (store.allWorkAreas) return { kind: 'all' };
  if (store.selectedNamespaceId)
    return { kind: 'namespace', namespaceId: store.selectedNamespaceId };
  return store.selectedProjectId
    ? { kind: 'work-area', workAreaId: store.selectedProjectId }
    : null;
}

export function originOf(
  location: Pick<Location, 'pathname' | 'search' | 'hash'>,
  scope: FullPageScopeType | null,
): IFullPageOrigin {
  return {
    pathname: location.pathname,
    search: location.search,
    hash: location.hash,
    historyIndex: historyIndex(),
    scope,
  };
}

/**
 * Whether the page on screen keeps the scope it was opened from instead of entering its work area
 * (I-1205, I-1357 IL-19j). A page opened with `cameFrom` does. So does an item's full page pushed
 * from a page of All work areas, however it was opened — a row, a link, a relation, a child, a
 * converted or merged item: its history entry says where it was pushed from (`historyBackLabel`), so
 * no caller has to remember to carry the state (I-0689: "remain in all work areas").
 * `scopeHeld.guard.test.ts` refuses a hold read any other way.
 */
export function scopeHeldAt(location: {
  readonly state: unknown;
  readonly key: string;
  readonly search: string;
}): boolean {
  if (readCameFrom(location.state) !== null) return true;
  if (new URLSearchParams(location.search).get('view') !== 'full') return false;
  const from = previousAddressOf(location.key);
  return from !== null && isWorkAreaOverviewPath(from.split(/[?#]/, 1)[0]);
}
